import { inject, Injectable } from '@angular/core';

import {
  AdditionalUserSelectionOption,
  AdditionalUserSelectionOptionKey,
  User,
} from '@core/account-types';
import { I18nService } from '@core/i18n-legacy';

@Injectable({ providedIn: 'root' })
export class AdditionalUserSelectionOptionsService {
  private readonly i18n = inject(I18nService);

  private readonly defaultOptions: AdditionalUserSelectionOption[] = [
    {
      key: AdditionalUserSelectionOptionKey.AdminIdsAddRecruiter,
      heading: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.recruiter.heading',
      ),
      subheading: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.recruiter.subheading',
      ),
      tooltip: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.recruiter.tooltip',
      ),
      isSelected: false,
    },
    {
      key: AdditionalUserSelectionOptionKey.AdminIdsAddHiringManager,
      heading: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.hiring_manager.heading',
      ),
      subheading: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.hiring_manager.subheading',
      ),
      tooltip: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.hiring_manager.tooltip',
      ),
      isSelected: false,
    },
    {
      key: AdditionalUserSelectionOptionKey.AdminIdsAddAdminPerformingAction,
      heading: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.admin_performing_action.heading',
      ),
      subheading: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.admin_performing_action.subheading',
      ),
      tooltip: this.i18n.translate(
        'app.automated_actions.action_editor.additional_user_selection_options.admin_performing_action.tooltip',
      ),
      isSelected: false,
    },
  ];

  public getOptions({
    withoutAdminPerformingAction,
    action,
    recruiter,
    hiringManager,
  }: {
    withoutAdminPerformingAction: boolean;
    action: {
      adminIdsAddHiringManager?: boolean;
      adminIdsAddRecruiter?: boolean;
      adminIdsAddAdminPerformingAction?: boolean;
    };
    recruiter: User | null;
    hiringManager: User | null;
  }): AdditionalUserSelectionOption[] {
    return this.getDefaultOptions(withoutAdminPerformingAction).map(option => {
      if (option.key === AdditionalUserSelectionOptionKey.AdminIdsAddHiringManager) {
        return {
          ...option,
          isSelected: !!action.adminIdsAddHiringManager,
          badge: !hiringManager
            ? {
                icon: 'warning',
                color: 'warning-tint',
                label: this.i18n.translate('app.common.not_assigned'),
              }
            : undefined,
        };
      }

      if (option.key === AdditionalUserSelectionOptionKey.AdminIdsAddRecruiter) {
        return {
          ...option,
          isSelected: !!action.adminIdsAddRecruiter,
          badge: !recruiter
            ? {
                icon: 'warning',
                color: 'warning-tint',
                label: this.i18n.translate('app.common.not_assigned'),
              }
            : undefined,
        };
      }

      if (option.key === AdditionalUserSelectionOptionKey.AdminIdsAddAdminPerformingAction) {
        return {
          ...option,
          isSelected: !!action.adminIdsAddAdminPerformingAction,
        };
      }

      return {
        ...option,
        isSelected: false,
        showNoAssignedBadge: false,
      };
    });
  }

  private getDefaultOptions(
    withoutAdminPerformingAction: boolean = false,
  ): AdditionalUserSelectionOption[] {
    if (withoutAdminPerformingAction) {
      return this.defaultOptions.filter(
        option => option.key !== AdditionalUserSelectionOptionKey.AdminIdsAddAdminPerformingAction,
      );
    }
    return this.defaultOptions;
  }
}
